import { useEffect } from 'react';

export const MAIN_CONTENT_ID = 'main-content';

type RouteFocusProps = {
   /** False on the initial document load, where the browser already starts at the top. */
   enabled: boolean;
};

/**
 * Moves focus to the <main> landmark once a page has mounted after an
 * in-app navigation (WCAG 2.4.3). Without this, keyboard and screen-reader
 * users stay parked wherever they were in the previous page's DOM (usually
 * the header link they activated) and the next Tab continues from there.
 *
 * Rendered inside <main> and keyed by pathname, so it re-runs per
 * navigation, after AnimatePresence has mounted the new page. It yields to
 * any focus the new page placed inside <main> itself (autoFocus fields,
 * dialogs) and never scrolls, so scroll restoration stays as it was.
 */
const RouteFocus = ({ enabled }: RouteFocusProps) => {
   useEffect(() => {
      if (!enabled) return;
      const frame = window.requestAnimationFrame(() => {
         const main = document.getElementById(MAIN_CONTENT_ID);
         if (!main) return;
         const active = document.activeElement;
         if (active && active !== document.body && main.contains(active)) return;
         main.focus({ preventScroll: true });
      });
      return () => window.cancelAnimationFrame(frame);
   }, [enabled]);

   return null;
};

export default RouteFocus;
